import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { type FC } from 'react'
import {
  Avatar,
  Box,
  Dropdown,
  DropdownContent,
  DropdownGroup,
  DropdownTextInput,
  EmptyState,
  Icon,
  Scrollbar,
  SpinnerLoader,
  Typography,
} from 'shared/ui'
import { Label } from 'shared/ui/Label'
import { makeTextFieldClear, makeTextFieldIcon, TextFieldPlaceholder } from 'shared/ui/TextField'
import { type TransferConversationModalStore } from 'widgets/TransferConversationModal/store/transferConversationModalStore'

import styles from '../styles.module.scss'

export interface IMembersDropdownProps {
  store: TransferConversationModalStore
}
export const MembersDropdown: FC<IMembersDropdownProps> = observer(({ store }) => {
  const {
    searchMembers,
    setSearchMembers,
    membersDropdownItems,
    setActiveMemberId,
    activeMember,
    activeInbox,
    isEmptyMembersDropdown,
    loadingMembers,
    canReassignConversations,
  } = store

  const TriggerComponent = () => {
    return (
      <TextFieldPlaceholder
        rightActions={[
          makeTextFieldIcon({
            icon: 'chevronDown',
            className: styles.iconAction,
          }),
        ]}
        placeholder={
          activeMember ? (
            <div className={styles.wrapValue}>
              <div className={styles.icon}>
                <Avatar info={activeMember.avatarInfo} size={16} online={!activeMember.isAway} />
              </div>
              <Typography
                className={styles.name}
                variant='body-md-regular'
                ellipsis
                pointer
                ariaLabel={'name'}
              >
                {activeMember.name}
              </Typography>
            </div>
          ) : (
            <div className={styles.wrapValue}>
              <div className={styles.icon}>
                <Icon icon={'settingsLine'} color='var(--content-primary-tertiary)' fontSize={16} />
              </div>
              <Typography
                className={styles.name}
                variant='body-md-regular'
                ellipsis
                pointer
                ariaLabel={'name'}
              >
                Inbox default settings
              </Typography>
            </div>
          )
        }
        className={classNames(styles.textField, {
          [styles.disabled]: !activeInbox || !canReassignConversations,
        })}
        variant={'stroke'}
        size={'medium'}
      />
    )
  }

  return (
    <div className={styles.wrapDropdown}>
      <Label
        label={{
          text: 'Select assignee',
        }}
        marginBottom={4}
      />
      <Dropdown
        width={432}
        ariaLabel={'membersDropdown'}
        disabled={!canReassignConversations}
        triggerComponent={TriggerComponent}
        customComponent={(onClose) => {
          if (!activeInbox) return null

          return (
            <DropdownContent noPadding>
              <DropdownTextInput
                textFieldProps={{
                  onChange: setSearchMembers,
                  value: searchMembers,
                  InputProps: {
                    placeholder: 'Search',
                  },
                  rightActions: [makeTextFieldClear()],
                }}
              />
              {loadingMembers ? (
                <Box height={41}>
                  <SpinnerLoader ariaLabel={'MembersDropdown'} size={16} />
                </Box>
              ) : (
                <Scrollbar autoHeight autoHeightMin={0} autoHeightMax={160}>
                  {isEmptyMembersDropdown ? (
                    <EmptyState noIcon boxProps={{ height: 41 }} />
                  ) : (
                    <DropdownGroup
                      key={'members'}
                      items={membersDropdownItems}
                      isScroll={false}
                      onChange={(value) => {
                        setActiveMemberId(value.id)
                        onClose()
                      }}
                    />
                  )}
                </Scrollbar>
              )}
            </DropdownContent>
          )
        }}
      />
    </div>
  )
})
